import type { FunctionComponent } from 'preact';
import { useRef } from 'preact/hooks';

import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import { useCollapsible } from 'Core/hooks/index';
import {
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
  selectedVehicleSelector,
} from 'Core/selectors/fitmentSearch/index';
import { searchResponseExtraSelector } from 'Core/selectors/search';
import { useSelector } from 'Core/store';
import { cloneSafe } from 'Utils/components';

import isMobile from '../../vendor/isMobile';
import AutoSyncVisualization, {
  type AutoSyncConfig,
  type Params as AutoSyncVisualizationParams,
  type Props as AutoSyncVisualizationProps,
} from './autoSyncVisualization';
import type { VehicleTemplateData } from './types';

interface WheelTireVehicleInfoExtra {
  WheelData?: WheelData;
  TireSizes?: string[];
}

interface WheelData {
  FrontAxle: WheelsAxleInfo;
  RearAxle?: WheelsAxleInfo;
  BoltPattern?: string;
  HubBore?: string;
  Hardware?: string;
  Thread?: string;
  Hex?: string;
  TPMS?: string;
  SeatType?: string;
}
interface WheelsAxleInfo {
  Diameter: string;
  Width: number;
  Offset: number;
}

interface Params extends VehicleTemplateData {
  collapsed: boolean;
  toggleCollapsed: () => void;
  data: WheelTireVehicleInfoExtra;
  vehicleImage?: string;
  autoSyncVisualization?: TemplateFunctionInvoker<AutoSyncVisualizationParams>;
}

interface Props {
  template: TemplateFunction<Params>;
  initCollapsed?: boolean;
  useAutoSyncVisualizer?: boolean;
  autoSyncConfig?: AutoSyncConfig;
}

const WheelTireVehicleInfo: FunctionComponent<Props> = ({
  template,
  initCollapsed,
  useAutoSyncVisualizer,
  autoSyncConfig = {},
}) => {
  const rootRef = useRef<HTMLElement>(null);
  const { isCollapsed: collapsed, toggle: toggleCollapsed } = useCollapsible(
    rootRef,
    null,
    initCollapsed ?? isMobile,
  );

  const selectedVehicle = useSelector(selectedVehicleSelector);
  const baseFitmentFields = useSelector(fitmentSearchBaseFieldsSelector);
  const extraFitmentFields = useSelector(fitmentSearchExtraFieldsSelector);

  const searchResponseExtra = useSelector(searchResponseExtraSelector);
  const wheelTireVehicleInfo = searchResponseExtra?.WheelTireVehicleInfo as
    | WheelTireVehicleInfoExtra
    | undefined;

  if (!wheelTireVehicleInfo) {
    return null;
  }

  const autoSyncVisualization = (templ: TemplateFunction<AutoSyncVisualizationParams>) => {
    if (!useAutoSyncVisualizer) {
      return null;
    }

    const props = {
      template: templ,
      config: autoSyncConfig,
    } satisfies AutoSyncVisualizationProps;

    return (<AutoSyncVisualization {...props} key="autosync-visualization" />) as TemplateResult;
  };

  const component = template.call({
    collapsed,
    toggleCollapsed,
    data: wheelTireVehicleInfo,
    vehicleImage: searchResponseExtra?.VehicleImage,
    vehicle: selectedVehicle.filteredFieldMap,
    vehicleBaseValues: selectedVehicle.getFilteredValues(baseFitmentFields),
    vehicleExtraValues: selectedVehicle.getFilteredValues(extraFitmentFields),
    vehicleString: selectedVehicle.toString(),
    autoSyncVisualization,
  });

  return cloneSafe(component, rootRef);
};

export default WheelTireVehicleInfo;
